import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";
import { redirect } from "@tanstack/react-router";
import { createHash, createHmac, timingSafeEqual } from "node:crypto";
import entitiesFilmII from "@/assets/entities-film-ii-first-cut.mp4.asset.json";
import entitiesFilmIIPoster from "@/assets/entities-film-ii-poster.jpg.asset.json";
import mysakiFilm from "@/assets/mysaki-full-film.mp4.asset.json";
import mysakiPoster from "@/assets/mysaki-poster.jpg.asset.json";
import pesoFilm from "@/assets/peso-do-sentir-film.mp4.asset.json";
import pesoPoster from "@/assets/peso-do-sentir-poster.jpg.asset.json";

type GuestSessionData = { unlocked?: boolean };

function sessionConfig() {
  return {
    password: process.env["GUEST_SESSION_SECRET"]!,
    name: "guest-session",
    maxAge: 60 * 60 * 24 * 14,
    cookie: { httpOnly: true, secure: true, sameSite: "none" as const, path: "/" },
  };
}

function matches(input: string, expected: string) {
  const a = createHash("sha256").update(input, "utf8").digest();
  const b = createHash("sha256").update(expected, "utf8").digest();
  return timingSafeEqual(a, b);
}

export type GuestFilm = {
  title: string;
  project: string;
  year?: string;
  provider: "vimeo" | "youtube" | "file";
  id: string;
  src?: string;
  poster?: string;
};

/** Full-length performance films — only ever returned to unlocked guests. */
const films: GuestFilm[] = [
  { title: "The Weight of Feeling", project: "The Weight of Feeling", provider: "youtube", id: "bPL12Cjxq-g" },
  { title: "O Peso do Sentir", project: "The Weight of Feeling", provider: "file", id: "peso-do-sentir", src: pesoFilm.url, poster: pesoPoster.url },
  { title: "Magdalena", project: "Appropriation", provider: "file", id: "mysaki", src: mysakiFilm.url, poster: mysakiPoster.url },
  { title: "Butoh Caipira", project: "Butoh Caipira", provider: "youtube", id: "nVCfoBHdhok" },
  { title: "Luiza Prado — Butoh Caipira", project: "Butoh Caipira", provider: "youtube", id: "Q5fC0wFYxtI" },
  { title: "Fungi", project: "Symbiogenesis", provider: "vimeo", id: "932934707" },
  { title: "Pudor", project: "Colonized Sexuality", provider: "vimeo", id: "233659229" },
  { title: "Pai, Afasta de Mim Esse Cálice", project: "Colonized Sexuality", provider: "vimeo", id: "143548773" },
  { title: "I Miss You", project: "Colonized Sexuality", provider: "vimeo", id: "235166337" },
  { title: "Estender", project: "Colonized Sexuality", provider: "vimeo", id: "144276523" },
  { title: "Tenro", project: "Colonized Sexuality", provider: "youtube", id: "tUMvGIZ-wvY" },
  { title: "The Painter", project: "Topographies of Violence", provider: "vimeo", id: "233700623" },
  { title: "Corpo Estranho", project: "Topographies of Violence", provider: "vimeo", id: "161196586" },
  { title: "Resistencia", project: "Discourses of Necropolitics", provider: "vimeo", id: "233473348" },
  { title: "O Estado", project: "Discourses of Necropolitics", provider: "vimeo", id: "272140565" },
  { title: "Castração", project: "Discourses of Necropolitics", provider: "youtube", id: "_RXjPwBflDc" },
  { title: "E, daí?", project: "Discourses of Necropolitics", provider: "vimeo", id: "424329715" },
  { title: "Indi-gestão", project: "Discourses of Necropolitics", provider: "vimeo", id: "181019759" },
  { title: "The Body State", project: "Discourses of Necropolitics", provider: "vimeo", id: "934314540" },
  { title: "Database — Hifa Cybe + Ana Vohs", project: "Appropriation", provider: "youtube", id: "sox8_Q9crog" },
  { title: "Neurotransition", project: "Neurotransition", provider: "vimeo", id: "1178952666" },
  { title: "Entities #2 — Irid Duo", project: "Entities", provider: "vimeo", id: "1176132299" },
  { title: "Entities — Film II (first cut)", project: "Entities", provider: "file", id: "entities-film-ii", src: entitiesFilmII.url, poster: entitiesFilmIIPoster.url },
  { title: "Devir", project: "Performance", provider: "youtube", id: "pV07KN5fXMA" },
  { title: "Feto", project: "Performance", provider: "youtube", id: "MavTOGcsbOI" },
  { title: "Mergulho", project: "Performance", provider: "vimeo", id: "931828331" },
  { title: "Reincarnate", project: "Performance", provider: "vimeo", id: "424335301" },
];

export const unlockGuestSession = createServerFn({ method: "POST" })
  .inputValidator((data: { password: string }) => ({ password: String(data?.password ?? "").slice(0, 200) }))
  .handler(async ({ data }) => {
    const expected = process.env["GUEST_PASSWORD"];
    if (!expected) throw new Error("Guest password is not configured");
    if (!matches(data.password, expected)) return { ok: false as const };
    const session = await useSession<GuestSessionData>(sessionConfig());
    await session.update({ unlocked: true });
    return { ok: true as const };
  });

export const getGuestFilms = createServerFn({ method: "GET" }).handler(async () => {
  const session = await useSession<GuestSessionData>(sessionConfig());
  if (!session.data.unlocked) throw redirect({ to: "/guest-session/login" });
  return { films: films.map((f) => ({ ...f, token: shareToken(f.id) })) };
});

/** Signed token that unlocks one film only, for single-video share links. */
function shareToken(id: string) {
  return createHmac("sha256", process.env["GUEST_SESSION_SECRET"]!).update(`film:${id}`).digest("base64url").slice(0, 22);
}

export const getSharedFilm = createServerFn({ method: "GET" })
  .inputValidator((d: { id: string; t: string }) => ({ id: String(d?.id ?? "").slice(0, 40), t: String(d?.t ?? "").slice(0, 64) }))
  .handler(async ({ data }) => {
    const film = films.find((f) => f.id === data.id);
    if (!film || !data.t || !matches(data.t, shareToken(film.id))) return { film: null };
    return { film };
  });

export const lockGuestSession = createServerFn({ method: "POST" }).handler(async () => {
  const session = await useSession<GuestSessionData>(sessionConfig());
  await session.clear();
  return { ok: true as const };
});
